import React, { useEffect, useMemo, useState } from 'react';
import { useSession } from '@clerk/clerk-react';
import { createClerkSupabaseClient } from '../../../lib/supabase.js';
import { formatNumber } from '../../../styles/tokens/formatters';
import SectionHeader from '../../../components/ui/fancy/SectionHeader';
import DashboardFilters from '../../../components/ui/fancy/DashboardFilters';
import GlobalSearch from '../../../components/ui/fancy/GlobalSearch';
import { KpiGrid } from '../../../components/ui/KpiCard';
import { YoolTable } from '../../../components/ui/YoolTable';
import { useNavigate, useSearchParams } from 'react-router-dom';

const ConversionesTablePage = ({ title, viewName, selectFields, columns, renderSummary, loadRows, defaultSortKey, defaultSortDirection = 'asc' }) => {
  const { session } = useSession();
  const supabase = useMemo(() => createClerkSupabaseClient(session), [session]);
  const navigate = useNavigate();

  // Enlace directo a un registro por su código (`?search=SU-…`). El listado normal
  // se recorta a los últimos 30 días, así que un registro más antiguo no aparecería:
  // cuando viene el parámetro se busca por código sobre toda la vista.
  const [searchParams, setSearchParams] = useSearchParams();
  const codigoEnlazado = (searchParams.get('search') || '').trim();

  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [last30DaysCount, setLast30DaysCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activePlans, setActivePlans] = useState([]);

  // Growth stats
  const [growthData, setGrowthData] = useState({ total: 0, lastPeriod: 0, percent: 0 });

  // Opciones dinámicas para filtros
  const [userAgentOptions, setUserAgentOptions] = useState([]);
  const [routeOptions, setRouteOptions] = useState([]);

  // Filtros y Ordenamiento
  const [filters, setFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: defaultSortKey || 'created_at', direction: defaultSortDirection });

  // Helper para normalizar rutas (idéntico al de vistas/page.jsx para consistencia)
  const normalizeRoute = (ruta) => {
    if (!ruta) return ruta;
    if (ruta.startsWith('/simulacion/')) return '/simulacion/:id';
    if (ruta.startsWith('/contratar/simulacion/')) return '/contratar/simulacion/:id';
    return ruta;
  };

  const formatRangeDate = (dateValue) => {
    if (!dateValue) return '';
    return new Date(dateValue).toLocaleDateString('es-CL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const normalizeRangeEnd = (rangeEnd) => {
    const end = new Date(rangeEnd);
    end.setHours(23, 59, 59, 999);
    return end;
  };

  // Cargar planes activos
  useEffect(() => {
    if (!session) return;
    const fetchPlans = async () => {
      const { data } = await supabase.from('planes').select('id, nombre').eq('activo', true);
      if (data) {
        setActivePlans(data.map(p => ({ value: p.id, label: p.nombre })));
      }
    };
    fetchPlans();
  }, [session, supabase]);

  useEffect(() => {
    if (!session) return;

    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        const now = new Date();
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        
        // Determinar periodos para comparación
        const rawRange = filters.dateRange?.from ? {
          from: filters.dateRange.from,
          to: filters.dateRange.to || now
        } : {
          from: thirtyDaysAgo,
          to: now
        };

        const currentRange = viewName === 'sesiones'
          ? { ...rawRange, to: normalizeRangeEnd(rawRange.to) }
          : rawRange;

        const diff = currentRange.to.getTime() - currentRange.from.getTime();
        const prevRange = {
          from: new Date(currentRange.from.getTime() - diff),
          to: new Date(currentRange.from.getTime() - 1)
        };

        if (loadRows) {
          const result = await loadRows({
            supabase,
            filters,
            sortConfig,
            currentRange,
            prevRange,
          });

          const customRows = Array.isArray(result?.rows) ? result.rows : [];
          const customTotalCount = result?.totalCount ?? customRows.length;
          const customCurrentCount = result?.last30DaysCount ?? customRows.length;
          const customPreviousCount = result?.growthData?.lastPeriod ?? 0;
          const customGrowthPercent = customPreviousCount > 0
            ? ((customCurrentCount - customPreviousCount) / customPreviousCount) * 100
            : 0;

          setTotalCount(customTotalCount);
          setLast30DaysCount(customCurrentCount);
          setGrowthData(result?.growthData || {
            total: customCurrentCount,
            lastPeriod: customPreviousCount,
            percent: customGrowthPercent,
          });
          setRows(customRows);

          if (Array.isArray(result?.userAgentOptions)) {
            setUserAgentOptions(result.userAgentOptions);
          }

          if (Array.isArray(result?.routeOptions)) {
            setRouteOptions(result.routeOptions);
          }

          return;
        }

        // Construir queries
        let queryTotal = supabase.from(viewName).select('id', { count: 'exact' }).limit(0);

        // Query para el periodo actual (KPI principal)
        let queryCurrent = supabase.from(viewName).select('id', { count: 'exact' })
          .gte('created_at', currentRange.from.toISOString())
          .lte('created_at', currentRange.to.toISOString())
          .limit(0);

        // Query para el periodo anterior (Comparación)
        let queryPrevious = supabase.from(viewName).select('id', { count: 'exact' })
          .gte('created_at', prevRange.from.toISOString())
          .lte('created_at', prevRange.to.toISOString())
          .limit(0);

        let queryList = supabase.from(viewName).select(selectFields);

        if (codigoEnlazado) {
          // Se llegó desde un enlace a un registro puntual: manda el código, no el rango.
          queryList = queryList.eq('codigo', codigoEnlazado);
        } else {
          queryList = queryList
            .gte('created_at', currentRange.from.toISOString())
            .lte('created_at', currentRange.to.toISOString());
        }

        if (viewName === 'sesiones_funnel') {
          queryTotal = queryTotal.not('datos_simulacion', 'is', null).neq('datos_simulacion', '{}');
          queryCurrent = queryCurrent.not('datos_simulacion', 'is', null).neq('datos_simulacion', '{}');
          queryPrevious = queryPrevious.not('datos_simulacion', 'is', null).neq('datos_simulacion', '{}');
          queryList = queryList.not('datos_simulacion', 'is', null).neq('datos_simulacion', '{}');
        }

        // Filtro por User Agent
        if (filters.userAgents?.length > 0) {
          queryList = queryList.in('user_agent', filters.userAgents);
          queryCurrent = queryCurrent.in('user_agent', filters.userAgents);
          queryPrevious = queryPrevious.in('user_agent', filters.userAgents);
        }

        // Filtro por Ruta Inicio (considerando normalización)
        if (filters.routes?.length > 0) {
          const conditions = filters.routes.map(route => {
            if (route === '/simulacion/:id') return `ruta_inicio.like./simulacion/%`;
            if (route === '/contratar/simulacion/:id') return `ruta_inicio.like./contratar/simulacion/%`;
            return `ruta_inicio.eq.${route}`;
          }).join(',');
          
          queryList = queryList.or(conditions);
          queryCurrent = queryCurrent.or(conditions);
          queryPrevious = queryPrevious.or(conditions);
        }

        // Ordenamiento y límites
        queryList = queryList.order(sortConfig.key, { ascending: sortConfig.direction === 'asc' });
        queryList = queryList.limit(200);

        // Ejecutar queries
        const [totalRes, currentRes, prevRes, listRes] = await Promise.all([
          queryTotal,
          queryCurrent,
          queryPrevious,
          queryList
        ]);

        if (totalRes.error) throw totalRes.error;
        if (currentRes.error) throw currentRes.error;
        if (prevRes.error) throw prevRes.error;
        if (listRes.error) throw listRes.error;

        setTotalCount(totalRes.count || 0);
        setLast30DaysCount(currentRes.count || 0);
        
        // Calcular crecimiento
        const currentCount = currentRes.count || 0;
        const prevCount = prevRes.count || 0;
        const growthPercent = prevCount > 0 ? ((currentCount - prevCount) / prevCount) * 100 : 0;
        
        setGrowthData({
          total: currentCount,
          lastPeriod: prevCount,
          percent: growthPercent
        });
        
        const data = Array.isArray(listRes.data) ? listRes.data : [];
        setRows(data);

        // Extraer opciones dinámicas para filtros (solo si están vacías)
        if (userAgentOptions.length === 0 && data.length > 0) {
          const uas = [...new Set(data.map(r => r.user_agent).filter(Boolean))];
          setUserAgentOptions(uas.map(ua => ({ 
            value: ua, 
            label: ua.length > 30 ? ua.substring(0, 30) + '...' : ua 
          })));
        }
        
        if (routeOptions.length === 0 && data.length > 0) {
          const rts = [...new Set(data.map(r => normalizeRoute(r.ruta_inicio)).filter(Boolean))];
          setRouteOptions(rts.map(r => ({ value: r, label: r })));
        }

      } catch (e) {
        setError(e?.message || 'Error cargando datos');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [session, supabase, viewName, selectFields, filters, sortConfig, loadRows, codigoEnlazado]);

  const handleNavigate = (path) => {
    navigate(path);
  };

  const handleSort = (key) => {
    setSortConfig((current) => ({
      key,
      direction: current.key === key && current.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const rangeLabel = filters.dateRange?.from
    ? `Del ${formatRangeDate(filters.dateRange.from)} al ${formatRangeDate(filters.dateRange.to || new Date())}`
    : 'Últimos 30 días';

  const defaultKpiItems = [
    {
      title: 'Total en rango',
      value: formatNumber(last30DaysCount),
      helper: rangeLabel,
      trend: 'neutral'
    },
    {
      title: 'Periodo anterior',
      value: formatNumber(growthData.lastPeriod),
      helper: 'Comparación equivalente',
      trend: growthData.percent >= 0 ? 'up' : 'down',
      delta: `${growthData.percent >= 0 ? '+' : ''}${formatNumber(Math.abs(growthData.percent))}% vs periodo actual`,
      accentClass: 'text-blue-600'
    }
  ];

  return (
    <div className="space-y-8">
      <SectionHeader 
        title={title} 
        subtitle="Panel de análisis y seguimiento"
        action={<GlobalSearch onNavigate={handleNavigate} />}
      />

      <DashboardFilters 
        onFilterChange={setFilters}
        products={activePlans}
        userAgents={userAgentOptions}
        routes={routeOptions}
        channels={[
          { value: 'web', label: 'Web Directa' },
          { value: 'fb', label: 'Facebook Ads' }
        ]}
        showClientFilter={false}
      />

      {/* Si renderSummary existe, dejamos que controle sus propios KPIs, si no, mostramos los default */}
      {renderSummary ? (
        renderSummary({ rows, loading, error, totalCount, last30DaysCount, filters, growthData })
      ) : (
        <KpiGrid items={defaultKpiItems} loading={loading} />
      )}

      {codigoEnlazado && (
        <div className="flex items-center gap-3 rounded-lg border px-4 py-2.5 text-sm">
          <span className="text-muted-foreground">Mostrando solo</span>
          <span className="font-mono font-medium">{codigoEnlazado}</span>
          <button
            type="button"
            className="ml-auto text-xs underline text-muted-foreground hover:text-foreground"
            onClick={() => {
              const resto = new URLSearchParams(searchParams);
              resto.delete('search');
              setSearchParams(resto, { replace: true });
            }}
          >
            Ver todos
          </button>
        </div>
      )}

      {error ? (
        <div className="text-sm py-4" style={{ color: 'var(--yool-error)' }}>{error}</div>
      ) : (
        <YoolTable
          columns={columns.map(c => ({ ...c, label: c.label ?? c.header, sortable: c.sortable !== false }))}
          rows={loading ? [] : rows}
          rowKey="id"
          search={{ placeholder: 'Buscar en tabla…' }}
          initialSort={defaultSortKey ? { key: defaultSortKey, dir: defaultSortDirection === 'desc' ? 'desc' : 'asc' } : undefined}
          empty={loading
            ? { icon: 'refresh', title: 'Cargando…', text: '' }
            : codigoEnlazado
              ? { title: `No se encontró ${codigoEnlazado}`, text: 'El código no existe en esta vista o no tienes acceso a él.' }
              : { title: 'Sin registros', text: 'Ajusta los filtros o el rango de fechas.' }
          }
        />
      )}
    </div>
  );
};

export default ConversionesTablePage;
